import { ref } from 'vue'

import { EVENT_CHAT, EVENT_LANGUAGE, CUSTOM_EVENT_CHAT } from '@shared/config/events'

import Events from '@shared/services/Events'

import useApplication from '@shared/hooks/useApplication'
import useI18n from '@shared/hooks/useI18n'

// GLOBAL DATA
let initialized = false

export default function useChat() {
  // LOCAL DATA
  const chatOpen = ref(false)
  const chatConversation = ref(false)
  const chatTriggered = ref(false)

  // HOOKS
  const { dispatchCustomEvent, pendingHTML } = useApplication()
  const { selectedLanguage } = useI18n()

  // METHODS
  function initChat() {
    if (!initialized && import.meta.env.VITE_FEATURE_CHAT) {
      initialized = true

      _initializeChat()
        .then(() => $_chatAddEventDispatcher())
        .then(() => $_chatAddEventListener())
        .then(() => {
          dispatchCustomEvent(CUSTOM_EVENT_CHAT.LANGUAGE, {
            language: selectedLanguage.value.toLowerCase()
          })

          if (chatOpen.value) {
            _openChat()
          }
        })
        .finally(_ => {
          Events.emit(EVENT_CHAT.LOADED, initialized)
        })
    }
  }

  function _initializeChat() {
    return (
      pendingHTML('.sanchat-wrapper')
        .then(_ => (initialized = true))
        // ON production we disable the chat if it not loaded
        .catch(_ => (initialized = import.meta.env.DEV))
    )
  }

  function _openChat(options) {
    if (initialized) {
      const eventOptions = options ?? { behavior: 'active' }
      dispatchCustomEvent(CUSTOM_EVENT_CHAT.OPEN, eventOptions)
    }
  }

  function $_chatAddEventDispatcher() {
    Events.on(EVENT_CHAT.OPEN, event => {
      chatOpen.value = true
      _openChat(event)
    })

    Events.on(EVENT_CHAT.NUDGING, () => {
      if (!chatTriggered.value) {
        chatTriggered.value = true
        dispatchCustomEvent(CUSTOM_EVENT_CHAT.TRIGGER_NUDGING)
      }
    })

    Events.on(EVENT_LANGUAGE.LOADED, ({ language }) => {
      dispatchCustomEvent(CUSTOM_EVENT_CHAT.LANGUAGE, { language: language.toLowerCase() })
    })

    return Promise.resolve()
  }

  function $_chatAddEventListener() {
    const listenTo = window.addEventListener

    // start sending basket to chat app
    listenTo(CUSTOM_EVENT_CHAT.CONVERSATION_STARTED, () => {
      chatConversation.value = true
    })

    // emit close event on chat closing
    listenTo(CUSTOM_EVENT_CHAT.CLOSE, () => {
      chatOpen.value = false
      Events.emit(EVENT_CHAT.CLOSE)
    })

    // disable watcher if chat is closed
    listenTo(CUSTOM_EVENT_CHAT.CONVERSATION_ENDED, () => {
      chatConversation.value = false
    })

    return Promise.resolve()
  }

  return {
    initChat,
  }
}
